import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, ListPlus, Plus, Trash2, UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api/client";
import TaskDialog from "@/components/TaskDialog";
import { Button } from "@/components/ui/button";

const TEAMS = ["Management", "Shoot Team", "Edit Team", "Digital Team", "Sales Team", "Finance Team", "HR + Admin"];
const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmt = (iso) => { const [, m, d] = iso.split("-"); return `${d}/${m}`; };

// Week-specific backlog inside Weekly Grid. Items also live in the main Backlog.
export default function WeeklyBacklogSection({ weekStart, team, users, currentUser, onTaskCreated }) {
  const ws = isoOf(weekStart);
  const fullAccess = !!(currentUser?.is_super_admin || currentUser?.is_admin);
  const [open, setOpen] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [addTeam, setAddTeam] = useState("");
  const [assignFor, setAssignFor] = useState(null);
  const teamParam = team && team !== "ALL" ? team : null;
  const effectiveTeam = fullAccess ? teamParam : currentUser?.team;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backlog", { params: { week_start: ws, ...(teamParam ? { team: teamParam } : {}) } });
      setItems(data);
    } catch { setItems([]); } finally { setLoading(false); }
  }, [ws, teamParam]);
  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { setAddTeam(effectiveTeam || ""); }, [effectiveTeam]);

  const add = async (e) => {
    e.preventDefault();
    const t = effectiveTeam || addTeam;
    if (!title.trim() || !t) { toast.error("Add a title and pick a department"); return; }
    try {
      await api.post("/backlog", { title: title.trim(), notes: notes.trim(), team: t, week_start: ws });
      setTitle(""); setNotes(""); toast.success("Added to this week's backlog"); load();
    } catch (x) { toast.error(formatApiError(x?.response?.data?.detail) || "Add failed"); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Remove "${it.title}" from the backlog?`)) return;
    try { await api.delete(`/backlog/${it.id}`); load(); } catch { toast.error("Remove failed"); }
  };
  const onAssigned = async () => {
    if (assignFor) { try { await api.delete(`/backlog/${assignFor.id}`); } catch { /* ignore */ } }
    setAssignFor(null); load(); onTaskCreated?.();
  };

  return (
    <div className="glass rounded-xl mb-3" data-testid="weekly-backlog-section">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="weekly-backlog-toggle"
        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold hover:bg-white/[0.03] transition-colors rounded-xl">
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <ListPlus size={15} className="text-[#E11D2E]" />
        <span>Backlog for this week</span>
        <span className="text-xs font-normal text-white/50">{teamParam || (fullAccess ? "All departments" : currentUser?.team)} · week of {fmt(ws)}</span>
        {open && !loading && <span className="ml-auto text-xs text-white/50" data-testid="weekly-backlog-count">{items.length} item{items.length === 1 ? "" : "s"}</span>}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <form onSubmit={add} className="flex flex-wrap gap-2" data-testid="weekly-backlog-form">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Backlog work for this week…" data-testid="weekly-backlog-title"
              className="flex-1 min-w-[220px] h-9 rounded-md border border-white/15 bg-transparent px-3 text-sm" />
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" data-testid="weekly-backlog-notes"
              className="flex-1 min-w-[180px] h-9 rounded-md border border-white/15 bg-transparent px-3 text-sm" />
            {!effectiveTeam && (
              <select value={addTeam} onChange={(e) => setAddTeam(e.target.value)} data-testid="weekly-backlog-team" className="h-9 rounded-md border border-white/15 bg-surface px-2 text-sm">
                <option value="">Department…</option>
                {TEAMS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
            <Button type="submit" data-testid="weekly-backlog-add" className="h-9 bg-[#E11D2E] hover:bg-[#F03041] text-white"><Plus size={14} className="mr-1" /> Add</Button>
          </form>
          {loading ? <div className="text-sm text-white/50 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
            : items.length === 0 ? <div className="text-sm text-white/50 border border-dashed border-white/15 rounded-md p-4 text-center" data-testid="weekly-backlog-empty">No backlog work for this week.</div>
            : (
              <ul className="divide-y divide-white/5 rounded-md border border-white/10">
                {items.map((it) => (
                  <li key={it.id} className="flex flex-wrap items-center gap-3 px-3 py-2" data-testid={`weekly-backlog-item-${it.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold">{it.title}
                        {it.week_start < ws && <span className="ml-2 text-xs font-medium text-orange-600 dark:text-orange-400" data-testid={`weekly-backlog-carried-${it.id}`}>from week of {fmt(it.week_start)}</span>}
                      </div>
                      <div className="text-xs text-white/55">{it.team}{it.notes ? ` · ${it.notes}` : ""} · added by {it.created_by_name}</div>
                    </div>
                    <Button size="sm" onClick={() => setAssignFor(it)} data-testid={`weekly-backlog-assign-${it.id}`} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white">
                      <UserPlus size={13} className="mr-1" /> Assign Work
                    </Button>
                    <button type="button" onClick={() => remove(it)} title="Remove from backlog" data-testid={`weekly-backlog-delete-${it.id}`}
                      className="h-8 w-8 grid place-items-center rounded-md text-white/50 hover:text-red-600 hover:bg-red-500/10"><Trash2 size={14} /></button>
                  </li>
                ))}
              </ul>
            )}
        </div>
      )}
      <TaskDialog
        open={!!assignFor}
        onOpenChange={(o) => { if (!o) setAssignFor(null); }}
        users={users.filter((u) => currentUser?.is_super_admin || u.team === (assignFor?.team || currentUser?.team))}
        existing={assignFor ? { title: assignFor.title, team: assignFor.team, description: assignFor.notes } : null}
        onSaved={onAssigned}
      />
    </div>
  );
}
